import { useEffect, useState } from 'react'
import { Loader2, XCircle, RefreshCw, CheckCircle } from 'lucide-react'
import { getLocalFirst, setLocalFirst, relaunch, type LocalFirstState } from '@/api/profiles'

interface LocalDatabaseToggleProps {
  proxyUrl: string
  proxyToken: string | null
}

// Provider-mode switch for the local-first database (localFirst in the
// install's local config). The choice applies on the next launch, so after a
// change this offers a restart instead of pretending it's already active.
export function LocalDatabaseToggle({ proxyUrl, proxyToken }: LocalDatabaseToggleProps) {
  const [state, setState] = useState<LocalFirstState | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingRestart, setPendingRestart] = useState(false)

  useEffect(() => {
    getLocalFirst(proxyUrl, proxyToken)
      .then(setState)
      .catch((err: Error) => setError(err.message))
  }, [proxyUrl, proxyToken])

  async function toggle() {
    if (!state) return
    const next = !state.enabled
    setSaving(true)
    setError(null)
    try {
      await setLocalFirst(proxyUrl, proxyToken, next)
      setState({ ...state, enabled: next })
      setPendingRestart(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  if (!state && !error) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="w-4 h-4 animate-spin text-white/25" />
      </div>
    )
  }

  const remote = state?.topology === 'remote'

  return (
    <div className="flex flex-col gap-4 pt-2">
      <p className="text-white/40 text-xs leading-relaxed">
        Con la base local activa, este equipo guarda y consulta todo en su propio disco y sigue funcionando sin internet.
        La nube queda como respaldo: los cambios se copian solos cuando hay conexión. La primera vez descarga todos los datos de la nube, lo que puede tardar unos minutos.
      </p>

      {state && (
        <label className={`flex items-center justify-between gap-4 border border-white/10 bg-white/4 rounded-2xl px-4 py-3 ${remote ? 'opacity-50' : 'cursor-pointer'}`}>
          <span className="flex flex-col gap-0.5">
            <span className="text-white/85 text-sm font-medium">Base de datos local</span>
            <span className="text-white/40 text-xs">
              {remote
                ? 'Solo disponible en el equipo del gimnasio (topología de red local).'
                : state.enabled ? 'Activa — la nube es el respaldo.' : 'Desactivada — la app trabaja directamente contra la nube.'}
            </span>
          </span>
          <input
            type="checkbox"
            role="switch"
            aria-label="Base de datos local"
            className="h-4 w-4 accent-emerald-500"
            checked={state.enabled}
            disabled={saving || (remote && !state.enabled)}
            onChange={toggle}
          />
        </label>
      )}

      {pendingRestart && (
        <div className="flex items-center justify-between gap-3 text-emerald-300 text-xs bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
          <span className="flex items-center gap-2">
            <CheckCircle className="w-3.5 h-3.5 shrink-0" />
            Guardado. El cambio se aplica al reiniciar la aplicación.
          </span>
          <button
            onClick={() => relaunch(proxyUrl, proxyToken)}
            className="flex items-center gap-1.5 font-medium text-emerald-200 hover:text-white transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            Reiniciar ahora
          </button>
        </div>
      )}

      {error && (
        <div role="alert" className="flex items-start gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          <XCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
          {error}
        </div>
      )}
    </div>
  )
}
